import { create } from 'zustand';
import { DeepSeekConfig } from '../types';
import { useLLMStore } from './llmStore';
import * as chatApi from '../services/chatApi';
import { buildTutorSystemPrompt } from '../utils/aiTutor';

// 出题只能基于"讲过的内容"，因此讲解本身必须还在上下文里。窗口过窄时，
// 早期的讲解会被截断，模型只能凭常识出题，这正是出现超纲题的原因之一。
const CONVERSATION_HISTORY_LIMIT = 16;

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  type: 'text';
}

interface ChatConversation {
  _id: string;
  title: string;
  messages: Message[];
  llmProvider: string;
  llmModel: string;
  isArchived: boolean;
  lastMessageAt: Date;
  createdAt: Date;
}

interface ChatStore {
  // Current active conversation
  currentConversation: ChatConversation | null;
  conversations: ChatConversation[];
  isLoading: boolean;
  isLoadingConversations: boolean;
  streamingMessageId: string | null; // ID of the message currently being streamed
  abortStreaming: () => void; // New action to abort streaming

  // 启发模式：助教先提问引导，而不是直接给答案
  socraticMode: boolean;
  setSocraticMode: (enabled: boolean) => void;

  // LLM config
  config: DeepSeekConfig;

  // Actions
  setCurrentConversation: (conversation: ChatConversation | null) => void;
  loadConversations: () => Promise<void>;
  createNewConversation: () => Promise<void>;
  loadConversation: (conversationId: string) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
  updateConversationTitle: (conversationId: string, title: string) => Promise<boolean>;

  // Message actions
  sendMessage: (content: string) => Promise<void>;
  clearMessages: () => void;
  setLoading: (loading: boolean) => void;
  setConfig: (config: Partial<DeepSeekConfig>) => void;
  generateTitle: (conversationId: string, firstMessage: string) => Promise<void>;
  updateStreamingMessage: (messageId: string, content: string) => void; // New action for streaming updates
}

// Module-level abort controller for streaming
let currentAbortController: AbortController | null = null;

export const useChatStore = create<ChatStore>((set, get) => ({
  currentConversation: null,
  conversations: [],
  isLoading: false,
  isLoadingConversations: false,
  streamingMessageId: null,
  socraticMode: false,
  config: {
    apiKey: localStorage.getItem('deepseekApiKey') || process.env.REACT_APP_DEEPSEEK_API_KEY || '',
    baseUrl: 'https://api.deepseek.com/v1',
    model: 'deepseek-chat'
  },

  abortStreaming: () => {
    if (currentAbortController) {
      currentAbortController.abort();
      currentAbortController = null;
    }
    // Also clear streaming state
    set({ streamingMessageId: null });
  },

  setSocraticMode: (enabled) => {
    set({ socraticMode: enabled });
  },

  setCurrentConversation: (conversation) => {
    set({ currentConversation: conversation });
  },

  loadConversations: async () => {
    set({ isLoadingConversations: true });
    try {
      console.log('Loading conversations...');
      const response = await chatApi.getConversations({ archived: false, limit: 50 });
      console.log('Conversations API response:', response);

      if (response.success && response.data) {
        console.log('Raw conversations data:', response.data);

        const conversations: ChatConversation[] = response.data.map((conv: any) => {
          console.log('Processing conversation:', conv);

          // Safely convert messages
          const messages = (conv.messages || []).map((msg: any) => {
            const timestamp = msg.timestamp instanceof Date
              ? msg.timestamp
              : new Date(msg.timestamp);

            return {
              id: `${conv._id}-${timestamp.getTime()}-${Math.random()}`,
              content: msg.content || '',
              sender: msg.role === 'user' ? 'user' : 'ai',
              timestamp,
              type: 'text' as const
            };
          });

          return {
            _id: conv._id,
            title: conv.title || '新对话',
            messages,
            llmProvider: conv.llmProvider || 'deepseek',
            llmModel: conv.llmModel || 'deepseek-chat',
            isArchived: conv.isArchived || false,
            lastMessageAt: conv.lastMessageAt ? new Date(conv.lastMessageAt) : new Date(),
            createdAt: conv.createdAt ? new Date(conv.createdAt) : new Date()
          };
        });

        console.log('Parsed conversations:', conversations);
        console.log('Setting conversations in store...');
        set({ conversations });
        console.log('Conversations set successfully');
      } else {
        console.log('Failed to load conversations:', response.message);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      set({ isLoadingConversations: false });
    }
  },

  createNewConversation: async () => {
    set({ isLoading: true });
    try {
      const response = await chatApi.createConversation();
      if (response.success && response.data) {
        const newConversation: ChatConversation = {
          _id: response.data._id,
          title: response.data.title,
          messages: [],
          llmProvider: response.data.llmProvider,
          llmModel: response.data.llmModel,
          isArchived: response.data.isArchived,
          lastMessageAt: new Date(response.data.lastMessageAt),
          createdAt: new Date(response.data.createdAt)
        };

        set(state => ({
          conversations: [newConversation, ...state.conversations],
          currentConversation: newConversation
        }));
      }
    } catch (error) {
      console.error('Failed to create conversation:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  loadConversation: async (conversationId: string) => {
    set({ isLoading: true });
    try {
      console.log('Loading conversation:', conversationId);
      const response = await chatApi.getConversation(conversationId);
      console.log('Conversation response:', response);

      if (response.success && response.data) {
        const data: any = response.data;

        // Safely convert messages
        const messages = (data.messages || []).map((msg: any) => {
          const timestamp = msg.timestamp instanceof Date
            ? msg.timestamp
            : new Date(msg.timestamp);

          return {
            id: `${data._id}-${timestamp.getTime()}-${Math.random()}`,
            content: msg.content || '',
            sender: msg.role === 'user' ? 'user' : 'ai',
            timestamp,
            type: 'text' as const
          };
        });

        const conversation: ChatConversation = {
          _id: data._id,
          title: data.title || '新对话',
          messages,
          llmProvider: data.llmProvider || 'deepseek',
          llmModel: data.llmModel || 'deepseek-chat',
          isArchived: data.isArchived || false,
          lastMessageAt: data.lastMessageAt ? new Date(data.lastMessageAt) : new Date(),
          createdAt: data.createdAt ? new Date(data.createdAt) : new Date()
        };

        console.log('Setting current conversation:', conversation);
        set({ currentConversation: conversation });
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  deleteConversation: async (conversationId: string) => {
    try {
      const response = await chatApi.deleteConversation(conversationId);
      if (response.success) {
        set(state => ({
          conversations: state.conversations.filter(c => c._id !== conversationId),
          currentConversation: state.currentConversation?._id === conversationId ? null : state.currentConversation
        }));
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  },

  updateConversationTitle: async (conversationId: string, title: string) => {
    try {
      console.log('Updating conversation title:', conversationId, 'to:', title);
      const response = await chatApi.updateConversationTitle(conversationId, title);
      console.log('Update title response:', response);

      if (response.success) {
        set(state => ({
          conversations: state.conversations.map(c =>
            c._id === conversationId ? { ...c, title } : c
          ),
          currentConversation: state.currentConversation?._id === conversationId
            ? { ...state.currentConversation!, title }
            : state.currentConversation
        }));
        console.log('Title updated successfully in store');
        return true;
      } else {
        console.error('Failed to update title:', response.message);
        return false;
      }
    } catch (error) {
      console.error('Failed to update title:', error);
      return false;
    }
  },

  sendMessage: async (content: string) => {
    const { currentConversation, config, loadConversations } = get();

    // Create a new conversation if none exists
    let activeConversation = currentConversation;
    if (!activeConversation) {
      await get().createNewConversation();
      activeConversation = get().currentConversation;
      if (!activeConversation) return;
    }

    // Add user message locally
    const userMessage: Message = {
      id: `${activeConversation._id}-${Date.now()}-user`,
      content,
      sender: 'user',
      timestamp: new Date(),
      type: 'text'
    };

    // Update local state
    set(state => ({
      currentConversation: state.currentConversation
        ? {
            ...state.currentConversation,
            messages: [...state.currentConversation.messages, userMessage],
            lastMessageAt: new Date()
          }
        : null,
      conversations: state.conversations.map(c =>
        c._id === activeConversation!._id
          ? { ...c, messages: [...c.messages, userMessage], lastMessageAt: new Date() }
          : c
      )
    }));

    // Save user message to database
    await chatApi.addMessage(activeConversation._id, 'user', content);

    set({ isLoading: true });

    try {
      // Try to load the active LLM configuration
      // Priority: LLM Store (user configured) > Local config (legacy)
      let actualConfig = config;

      try {
        const llmStore = useLLMStore.getState();
        await llmStore.loadConfig();

        if (llmStore.config && llmStore.config.apiKey && llmStore.config.apiKey !== 'YOUR_DEEPSEEK_API_KEY') {
          actualConfig = llmStore.config as any;
          console.log('[CHAT] Using LLM Store config:', {
            provider: llmStore.config.provider,
            name: llmStore.config.name,
            model: llmStore.config.model
          });
        } else if (!config.apiKey || config.apiKey === 'YOUR_DEEPSEEK_API_KEY') {
          throw new Error('请先配置LLM API密钥');
        } else {
          console.log('[CHAT] Using local config (legacy fallback)');
        }
      } catch (error) {
        console.error('[CHAT] Failed to load LLM config, falling back to local config:', error);
        if (!config.apiKey || config.apiKey === 'YOUR_DEEPSEEK_API_KEY') {
          throw new Error('请先配置LLM API密钥');
        }
      }

      let aiResponse = '';

      // Get the updated conversation with the new user message
      const updatedConversation = get().currentConversation;
      const messagesForApi = updatedConversation?.messages || [];

      // Require LLM API configuration - no preset fallback
      if (!actualConfig.apiKey) {
        throw new Error('请先配置LLM API密钥');
      }

      // Create AI message with empty content initially for streaming
      const aiMessageId = `${activeConversation._id}-${Date.now()}-ai`;
      const aiMessage: Message = {
        id: aiMessageId,
        content: '',
        sender: 'ai',
        timestamp: new Date(),
        type: 'text'
      };

      // Add empty AI message to local state first
      set(state => ({
        currentConversation: state.currentConversation
          ? {
              ...state.currentConversation,
              messages: [...state.currentConversation.messages, aiMessage],
              lastMessageAt: new Date()
            }
          : null,
        conversations: state.conversations.map(c =>
          c._id === activeConversation!._id
            ? { ...c, messages: [...c.messages, aiMessage], lastMessageAt: new Date() }
            : c
        ),
        streamingMessageId: aiMessageId
      }));

      // Create abort controller for this streaming request
      currentAbortController = new AbortController();

      try {
        // Call streaming API
        aiResponse = await callLLMAPIStream(
          content,
          messagesForApi,
          actualConfig,
          (chunk) => {
            // Update the streaming message content incrementally
            get().updateStreamingMessage(aiMessageId, chunk);
          },
          currentAbortController.signal // Pass abort signal
        );
      } catch (error) {
        // If aborted, don't save partial response
        if (error instanceof Error && error.name === 'AbortError') {
          set({ streamingMessageId: null, isLoading: false });
          return;
        }
        throw error;
      }

      // Clear streaming state and abort controller (content is already accumulated in the message)
      set({ streamingMessageId: null });
      currentAbortController = null;

      // Save AI message to database
      await chatApi.addMessage(activeConversation._id, 'assistant', aiResponse);

      // Generate title for new conversations BEFORE reloading from database
      // This checks the current local state before it gets overwritten by the database reload
      const localConvBeforeReload = get().currentConversation;
      console.log('Checking title generation. Local conversation:', localConvBeforeReload ? { id: localConvBeforeReload._id, title: localConvBeforeReload.title, messageCount: localConvBeforeReload.messages.length } : 'NO CURRENT CONV');

      const shouldGenerateTitle = localConvBeforeReload &&
        localConvBeforeReload.title === '新对话' &&
        localConvBeforeReload.messages.length === 2; // Just added user message + AI response = 2 messages

      if (shouldGenerateTitle) {
        // This is a new conversation without a proper title, generate one
        console.log('Generating title for new conversation:', activeConversation._id);
        await get().generateTitle(activeConversation._id, content);
      } else {
        console.log('Skipping title generation. Reasons:', {
          hasConv: !!localConvBeforeReload,
          title: localConvBeforeReload?.title,
          messageCount: localConvBeforeReload?.messages.length,
          isDefaultTitle: localConvBeforeReload?.title === '新对话',
          isFirstMessage: localConvBeforeReload?.messages.length === 2
        });
      }

      // Reload conversations to get the latest state from database (including the newly generated title)
      console.log('About to reload conversations...');
      await loadConversations();
      console.log('Conversations reloaded');

      // Update currentConversation from the freshly loaded conversations
      const state = get();
      const activeConvId = activeConversation?._id;
      console.log('Active conversation ID:', activeConvId);
      console.log('Conversations in state:', state.conversations.length);

      if (activeConvId) {
        const updatedConv = state.conversations.find(c => c._id === activeConvId);
        console.log('Found updated conversation:', updatedConv ? updatedConv.title : 'NOT FOUND');
        if (updatedConv) {
          set({ currentConversation: updatedConv });
        }
      }

    } catch (error) {
      // Show error message to user
      const errorMessage = error instanceof Error ? error.message : '发送消息失败，请检查API配置';

      const aiMessage: Message = {
        id: `${activeConversation._id}-${Date.now()}-ai`,
        content: `❌ ${errorMessage}`,
        sender: 'ai',
        timestamp: new Date(),
        type: 'text'
      };

      set(state => ({
        currentConversation: state.currentConversation
          ? {
              ...state.currentConversation,
              messages: [...state.currentConversation.messages, aiMessage],
              lastMessageAt: new Date()
            }
          : null,
        conversations: state.conversations.map(c =>
          c._id === activeConversation!._id
            ? { ...c, messages: [...c.messages, aiMessage], lastMessageAt: new Date() }
            : c
        )
      }));

      // Save error message to database
      await chatApi.addMessage(activeConversation._id, 'assistant', aiMessage.content);
    } finally {
      set({ isLoading: false });
    }
  },

  clearMessages: () => {
    // This creates a new conversation instead of clearing
    get().createNewConversation();
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  setConfig: (newConfig: Partial<DeepSeekConfig>) => {
    set(state => ({
      config: { ...state.config, ...newConfig }
    }));
    if (newConfig.apiKey) {
      localStorage.setItem('deepseekApiKey', newConfig.apiKey);
    }
  },

  generateTitle: async (conversationId: string, firstMessage: string) => {
    try {
      console.log('generateTitle called for conversation:', conversationId);

      // Truncate message for LLM (in case it's very long)
      const truncatedMessage = firstMessage.trim().substring(0, 200);

      // Try to load LLM config
      let config = get().config;
      const llmStore = useLLMStore.getState();
      await llmStore.loadConfig();

      if (llmStore.config && llmStore.config.apiKey) {
        config = llmStore.config as any;
      }
      console.log('LLM config for title generation:', { baseUrl: config.baseUrl, model: config.model, hasApiKey: !!config.apiKey });

      // If we have a valid API key, use LLM to generate title
      if (config.apiKey && config.apiKey !== 'YOUR_DEEPSEEK_API_KEY') {
        try {
          console.log('Calling LLM API for title generation...');
          const response = await fetch(`${config.baseUrl}/chat/completions`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${config.apiKey}`
            },
            body: JSON.stringify({
              model: config.model,
              messages: [
                {
                  role: 'system',
                  content: '你是一个对话标题生成助手。请根据用户的第一条消息，生成一个简洁的对话标题（不超过20个汉字）。只返回标题，不要有任何其他文字。'
                },
                {
                  role: 'user',
                  content: `请为以下对话生成一个简洁标题（不超过20个汉字）：\n${truncatedMessage}`
                }
              ],
              max_tokens: 50,
              temperature: 0.7
            })
          });

          console.log('LLM API response status:', response.status);
          if (response.ok) {
            const data = await response.json();
            console.log('LLM API response data:', data);
            if (data.choices && data.choices[0] && data.choices[0].message) {
              let title = data.choices[0].message.content.trim();
              // Remove quotes if present
              title = title.replace(/^["']|["']$/g, '').substring(0, 40);
              console.log('Generated title from LLM:', title);
              const success = await get().updateConversationTitle(conversationId, title);
              console.log('Title update result:', success);
              return;
            }
          } else {
            console.error('LLM API request failed:', response.status, response.statusText);
          }
        } catch (error) {
          console.error('Failed to generate title with LLM:', error);
        }
      } else {
        console.log('No valid API key for title generation, using fallback');
      }

      // Fallback: Generate a simple title based on the first message
      let fallbackTitle = truncatedMessage.substring(0, 30);
      if (truncatedMessage.length > 30) {
        fallbackTitle += '...';
      }
      console.log('Using fallback title:', fallbackTitle);
      await get().updateConversationTitle(conversationId, fallbackTitle);
    } catch (error) {
      console.error('Failed to generate title:', error);
    }
  },

  updateStreamingMessage: (messageId: string, newContent: string) => {
    set(state => {
      if (!state.currentConversation) return state;

      // Check if this is a chunk update or final content
      const isStreaming = state.streamingMessageId === messageId;

      // Find and update the streaming message
      const updatedMessages = state.currentConversation.messages.map(msg => {
        if (msg.id === messageId) {
          if (isStreaming) {
            // Accumulate content for streaming
            return { ...msg, content: msg.content + newContent };
          } else {
            // Replace content for final update
            return { ...msg, content: newContent };
          }
        }
        return msg;
      });

      return {
        currentConversation: {
          ...state.currentConversation,
          messages: updatedMessages
        }
      };
    });
  }
}));

// Call LLM API with streaming support
async function callLLMAPIStream(
  content: string,
  messages: Message[],
  config: DeepSeekConfig,
  onChunk: (chunk: string) => void,
  signal?: AbortSignal
): Promise<string> {
  try {
    const response = await fetch(`${config.baseUrl}/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.apiKey}`
      },
      body: JSON.stringify({
        model: config.model,
        messages: [
          {
            role: 'system',
            content: buildTutorSystemPrompt({
              variant: 'stats',
              mode: useChatStore.getState().socraticMode ? 'socratic' : 'explain',
              // messages excludes the message being sent, so count it too
              turnCount: messages.length + 1
            })
          },
          ...messages.map(msg => ({
            role: msg.sender === 'user' ? 'user' : 'assistant',
            content: msg.content
          })).slice(-CONVERSATION_HISTORY_LIMIT),
          {
            role: 'user',
            content
          }
        ],
        max_tokens: 4000,
        temperature: 0.7,
        stream: true
      }),
      signal // Add abort signal
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    const decoder = new TextDecoder();
    let fullContent = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n').filter(line => line.trim() !== '');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              fullContent += content;
              onChunk(content);
            }
          } catch (e) {
            // Skip invalid JSON
          }
        }
      }
    }

    return fullContent;
  } catch (error) {
    console.error('LLM API error:', error);
    throw error;
  }
}
